/**
 * Lightweight autoplay for Swiper sliders (hero slider, customer reviews).
 * Swiper 11's built-in autoplay runs a requestAnimationFrame loop on every frame while it waits (to report the
 * time left), which keeps the phone's main thread busy for the whole visit. This uses one timer per slide instead.
 * Same behaviour as before: keeps going after the visitor swipes or clicks, pauses while a mouse is over the
 * slider. It also pauses while the tab is hidden or the slider is off screen.
 *
 *
 * waitForInteraction (hero): the first slide change waits for the visitor's first scroll, tap, key or mouse move.
 * Browsers measure Largest Contentful Paint only until that first input, so a later slide (on phones the clearance
 * banner is shown whole, a little smaller than the other slides) can no longer replace the first slide as the
 * page's LCP. Without this, PageSpeed reported the LCP at 20s+ on mobile: the time the 3rd slide came in.
 *
 * Usage: const autoplay = window.mpAutoplay(swiper, hostElement, delayMs, { waitForInteraction: true });
 *        autoplay.stop(); autoplay.start();
 */
(function () {
  if (window.mpAutoplay) return;

  window.mpAutoplay = function (swiper, host, delay, options) {
    let timer = null;
    let hovering = false;
    let visible = true;
    let stopped = false;
    let waiting = !!(options && options.waitForInteraction) && !(window.Shopify && window.Shopify.designMode);

    const clear = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };
    const canRun = () => !stopped && !waiting && !hovering && visible && !document.hidden && !swiper.destroyed;

    if (waiting) {
      const inputEvents = ['pointerdown', 'keydown', 'scroll', 'wheel', 'touchstart', 'mousemove'];
      const listenerOptions = { passive: true, capture: true };
      const begin = () => {
        if (!waiting) return;
        waiting = false;
        inputEvents.forEach((name) => window.removeEventListener(name, begin, listenerOptions));
        schedule();
      };
      inputEvents.forEach((name) => window.addEventListener(name, begin, listenerOptions));
    }
    const schedule = () => {
      clear();
      if (!canRun()) return;
      timer = setTimeout(() => {
        timer = null;
        if (!canRun()) return;
        if (swiper.params.loop || !swiper.isEnd) {
          swiper.slideNext();
        } else {
          swiper.slideTo(0);
        }
      }, delay);
    };

    // Restart the wait after every slide change, including ones the visitor made
    swiper.on('slideChangeTransitionEnd', schedule);
    swiper.on('touchStart', clear);
    swiper.on('touchEnd', schedule);
    swiper.on('destroy', () => {
      stopped = true;
      clear();
    });

    host.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      hovering = true;
      clear();
    });
    host.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      hovering = false;
      schedule();
    });
    document.addEventListener('visibilitychange', schedule);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        visible = entries[entries.length - 1].isIntersecting;
        schedule();
      }).observe(host);
    }

    schedule();

    return {
      start() {
        stopped = false;
        schedule();
      },
      stop() {
        stopped = true;
        clear();
      },
    };
  };
})();
